// One input per field kind (pr/card/fields.py). The EIN is never sent back by the server: once saved it shows
// "On file, ending 6789" and a Replace button instead of the value.
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Address, FieldDef, Person } from './types'

type Props = {
  def: FieldDef
  value: unknown
  onChange: (v: unknown) => void
  onBlur?: () => void
  error?: string
  hint?: string | null     // the EIN on file ("ending 6789")
  testid: string
  newPerson?: () => Person // people: what "Add a person" starts with
  section?: string         // autofill section of another person's lines (owner-1), so the browser fills only these
}

/** Shorter money ranges on the form only, so each fits one line in a box (emails and staff pages keep the long ones). */
const SHORT: Record<string, string> = {
  under_1k: 'Under $1K', '1k_5k': '$1K–$5K', '5k_25k': '$5K–$25K', '25k_100k': '$25K–$100K', over_100k: 'Over $100K',
  under_50k: 'Under $50K', '50k_250k': '$50K–$250K', '250k_1m': '$250K–$1M', '1m_5m': '$1M–$5M', over_5m: 'Over $5M',
}

const EMPTY_ADDRESS: Address = { line1: '', line2: '', city: '', state: '', zip: '' }

/** "4,000", "$4000", "1200.50", "120k" or "$1.2M" -> cents, rounded to whole dollars; anything else -> null. A comma
 *  only groups thousands and a dot takes at most two decimals, so "1,5M" and "250.000" (a comma or dot the other way
 *  round) are asked again, never read as $15M or $250. */
export function dollarsToCents(s: string): number | null {
  const m = /^(\d{1,12}|\d{1,3}(?:,\d{3})+)(\.\d{1,2})?([km])?$/i.exec(s.replace(/[$\s]/g, ''))
  if (!m) return null
  const scale = !m[3] ? 1 : m[3].toLowerCase() === 'k' ? 1_000 : 1_000_000
  return Math.round(Number(m[1].replace(/,/g, '') + (m[2] ?? '')) * scale) * 100
}

export function centsToDollars(n: unknown): string {
  return typeof n === 'number' ? (n / 100).toLocaleString('en-US', { maximumFractionDigits: 0 }) : ''
}

function phoneOk(t: string): boolean {
  const digits = t.replace(/\D/g, '')
  const abroad = /^\s*\+/.test(t) && !/^\s*\+\s*1/.test(t)
  return abroad ? digits.length >= 8 && digits.length <= 15 : /^1?\d{10}$/.test(digits)
}

/** What's wrong with a value the server would refuse, in one sentence, or null when it can go up. These stay on the
 *  page while someone is typing them; a blur or Send shows the sentence (CHANGE-063). */
export function unfinished(def: FieldDef, v: unknown): string | null {
  if (v === null || v === '' || v === undefined) return null
  const text = typeof v === 'string' ? v : ''
  switch (def.kind) {
    case 'ein': return /^\d{2}[\s\u2010-\u2015-]*\d{7}$/.test(text.match(/\d[\d\s\u2010-\u2015-]*\d/)?.[0] ?? '') ? null : 'Enter the 9 digits on your IRS letter.'
    case 'phone': return phoneOk(text) ? null : 'Enter a 10-digit US number, or + and the country code.'
    case 'email': return /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(text.trim()) ? null : 'Enter an email address, like sam@yourcompany.com.'
    case 'money': return typeof v === 'number' ? null : 'Enter whole dollars, like 120000.'
    case 'percent': case 'integer': return typeof v === 'number' ? null : 'Enter a whole number, like 33.'
    case 'people': {
      const people = Array.isArray(v) ? (v as Person[]) : []
      for (const [i, person] of people.entries()) {
        for (const f of def.fields ?? []) {
          const why = unfinished(f, person[f.key])
          if (why) return `Owner ${i + 1}: ${why[0].toLowerCase()}${why.slice(1)}`
        }
      }
      return null
    }
    default: return null
  }
}

export function readyToSave(def: FieldDef, v: unknown): boolean {
  return unfinished(def, v) === null
}

export function newPersonId(): string {
  return `p-${crypto.randomUUID().slice(0, 12)}`
}

/** Is a field shown, given the other answers (show_if from the server)? */
export function shownFor(def: FieldDef, answers: Record<string, unknown>): boolean {
  return (def.show_if ?? []).every(([key, want]) => {
    const have = answers[key] ?? null
    if (want === '*') return have !== null && have !== ''
    if (typeof want === 'string' && want.startsWith('!')) return have !== want.slice(1)
    return have === want
  })
}

function Wrap({ def, children, testid, error, inline, confirm }: {
  def: FieldDef; children: ReactNode; testid: string; error?: string; inline?: boolean; confirm?: boolean
}) {
  return (
    <div className="card-field" data-testid={`${testid}-field`}>
      <label className={inline ? `card-check card-confirm${confirm ? ' is-on' : ''}` : 'card-line'}>
        {/* the box first, its name under it (founder, 2026-11-02: a name above read as the box above's) */}
        {children}
        <span className="card-label">{def.label}{def.required || inline ? '' : <span className="card-opt"> (optional)</span>}</span>
      </label>
      {error ? <p className="negative card-error" role="alert" data-testid={`${testid}-error`}>{error}</p> : null}
    </div>
  )
}

function Group({ def, children, testid, error }: { def: FieldDef; children: ReactNode; testid: string; error?: string }) {
  return (
    <fieldset className="card-field card-group" data-testid={`${testid}-field`}>
      <legend className="card-label">{def.label}{def.required ? '' : <span className="card-opt"> (optional)</span>}</legend>
      {children}
      {/* the name shown above the choices; the legend stays for screen readers */}
      <span className="card-label card-under" aria-hidden="true">{def.label}{def.required ? '' : <span className="card-opt"> (optional)</span>}</span>
      {error ? <p className="negative card-error" role="alert" data-testid={`${testid}-error`}>{error}</p> : null}
    </fieldset>
  )
}

/** A whole number typed as text: "1,200" -> 1200, "48%" -> 48; a half-typed value stays a string until it's whole. */
function wholeOrText(t: string): unknown {
  const s = t.trim().replace(/[,%\s]/g, '')
  return s === '' ? null : /^\d{1,9}$/.test(s) ? Number(s) : t
}

// Let the browser fill what it knows (WHATWG autocomplete tokens). The company's name, address and phone share one
// section, so picking a saved address fills them together; the person in charge and each owner get their own section,
// so filling one never writes the applicant's name into another person's lines. Anything not listed is off.
const AUTOFILL: Record<string, string> = {
  applicant_name: 'name', applicant_title: 'organization-title',
  legal_name: 'section-business organization', website: 'section-business url', business_phone: 'section-business work tel',
  rep_first_name: 'section-rep given-name', rep_last_name: 'section-rep family-name',
  rep_title: 'section-rep organization-title', rep_email: 'section-rep work email',
}
/** An owner's lines: `section-owner-N` plus the token. */
const PERSON_AUTOFILL: Record<string, string> = { first_name: 'given-name', last_name: 'family-name', title: 'organization-title', email: 'email' }
/** The name= browsers and password managers read when they don't trust autocomplete (Safari, 1Password). */
const NAMES: Record<string, string> = {
  applicant_name: 'name', applicant_title: 'job-title', legal_name: 'organization', website: 'website', business_phone: 'phone',
  rep_first_name: 'rep-first-name', rep_last_name: 'rep-last-name', rep_title: 'rep-job-title', rep_email: 'rep-email',
}
function autofillFor(def: FieldDef, section?: string): string {
  if (section) return PERSON_AUTOFILL[def.key] ? `section-${section} ${PERSON_AUTOFILL[def.key]}` : 'off'
  return AUTOFILL[def.key] ?? 'off'
}
/** Stops password managers that ignore autocomplete="off" from guessing (1Password, LastPass, Bitwarden). */
const NO_MANAGER = { 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-bwignore': true, 'data-form-type': 'other' }

/** US states: the value and what the box shows stay the code (CA); the option's text is the full name, so a saved
 *  "California" or "CA" both match when the browser or a password manager fills the state. */
const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska',
  NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
}
/** A filled state that isn't one of the codes ("California", "ca", "Calif."): the code, else what was given. */
export function stateCode(v: string): string {
  const t = v.trim()
  if (STATE_NAMES[t.toUpperCase()]) return t.toUpperCase()
  const low = t.toLowerCase().replace(/\.$/, '')
  return Object.keys(STATE_NAMES).find((c) => STATE_NAMES[c].toLowerCase() === low) ?? v
}

/** The EIN as it's typed (founder, 2026-11-02): digits only, with the dash put in after the first two. Deleting
 *  back over the dash takes the digit before it, so the box never sticks at "12-". */
export function einShape(typed: string, before: string): string {
  // an SSN (3-2-4) stays as typed, so the check below says it isn't an EIN instead of reshaping it into one
  if (/\d{3}[\s\u2010-\u2015-]+\d{2}[\s\u2010-\u2015-]+\d{4}/.test(typed)) return typed.trim()
  let d = typed.replace(/\D/g, '').slice(0, 9)
  if (before.endsWith('-') && typed === before.slice(0, -1)) d = d.slice(0, -1)
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d.length === 2 && typed.length > before.length ? `${d}-` : d
}

export function FieldInput(p: Props) {
  const { def, value, onChange, onBlur, error, hint, testid, section } = p
  const [replacing, setReplacing] = useState(false)
  const [money, setMoney] = useState(() => centsToDollars(value))
  // a string is what they're still typing ("1."): it stays in the box
  useEffect(() => { if (def.kind === 'money' && typeof value !== 'string' && dollarsToCents(money) !== value) setMoney(centsToDollars(value)) }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  if (def.sensitive && hint && !replacing && (value === undefined || value === null || value === '')) {
    return (
      <Wrap def={def} testid={testid} error={error}>
        <span className="card-onfile" data-testid={`${testid}-onfile`}>
          On file, {hint}.{' '}
          <button type="button" className="e-btn-text" data-testid={`${testid}-replace`} onClick={() => setReplacing(true)}>Replace</button>
        </span>
      </Wrap>
    )
  }
  const text = (v: unknown) => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v))
  const ac = autofillFor(def, section)
  const common = { 'data-testid': testid, id: testid, name: section ? `${section}-${def.key}` : NAMES[def.key] ?? def.key, onBlur,
    'aria-invalid': error ? true : undefined, autoComplete: ac, ...(ac === 'off' ? NO_MANAGER : {}) }
  const w = (child: ReactNode) => <Wrap def={def} testid={testid} error={error}>{child}</Wrap>
  switch (def.kind) {
    case 'text':
      return w(<input {...common} {...(def.key === 'website' ? { inputMode: 'url' as const, autoCapitalize: 'none', spellCheck: false } : {})} value={text(value)} maxLength={def.max_len} onChange={(e) => onChange(e.target.value)} />)
    case 'long':
      return w(<textarea {...common} rows={3} value={text(value)} maxLength={def.max_len} onChange={(e) => onChange(e.target.value)} />)
    case 'select':
      // A short list shows every choice as a tile, one click (NN/g, Baymard: no drop-down for a handful of options).
      if ((def.options ?? []).length <= 8) return (
        <Group def={def} testid={testid} error={error}>
          <div className="card-tiles" role="radiogroup" data-testid={testid} aria-invalid={error ? true : undefined}>
            {(def.options ?? []).map((o) => (
              <label key={o.value} className={`card-tile${text(value) === o.value ? ' is-on' : ''}`}>
                <input type="radio" name={testid} value={o.value} autoComplete="off" data-testid={`${testid}-${o.value}`} checked={text(value) === o.value}
                  onBlur={onBlur} onChange={() => onChange(o.value)} />
                <span>{SHORT[o.value] ?? o.label}</span>
              </label>
            ))}
          </div>
        </Group>
      )
      return w(
        <select {...common} value={text(value)} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Choose</option>
          {(def.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>,
      )
    case 'multiselect': {
      // only choices on today's list (an old draft may hold ones that were renamed; the server maps those)
      const picked = Array.isArray(value) ? (value as string[]).filter((x) => (def.options ?? []).some((o) => o.value === x)) : []
      return (
        <Group def={def} testid={testid} error={error}>
          <div className="card-tiles">
            {(def.options ?? []).map((o) => (
              <label key={o.value} className={`card-tile card-tile-check${picked.includes(o.value) ? ' is-on' : ''}`}>
                <input type="checkbox" name={testid} value={o.value} autoComplete="off" data-testid={`${testid}-${o.value}`} checked={picked.includes(o.value)} onBlur={onBlur}
                  onChange={(e) => onChange(e.target.checked ? [...picked, o.value] : picked.filter((x) => x !== o.value))} />
                <span>{SHORT[o.value] ?? o.label}</span>
              </label>
            ))}
          </div>
        </Group>
      )
    }
    case 'yes_no':
      return (
        <Group def={def} testid={testid} error={error}>
          <div className="card-tiles card-tiles-two" role="radiogroup">
            {([true, false] as const).map((b) => (
              <label key={String(b)} className={`card-tile${value === b ? ' is-on' : ''}`}>
                <input type="radio" name={testid} value={b ? 'yes' : 'no'} autoComplete="off" data-testid={`${testid}-${b ? 'yes' : 'no'}`} checked={value === b} onBlur={onBlur}
                  onChange={() => onChange(b)} />
                <span>{b ? 'Yes' : 'No'}</span>
              </label>
            ))}
          </div>
        </Group>
      )
    case 'date':
      return w(<input {...common} type="date" value={text(value)} onChange={(e) => onChange(e.target.value || null)} />)
    case 'month':
      return w(<input {...common} type="month" value={text(value)} onChange={(e) => onChange(e.target.value || null)} />)
    case 'ein':
      return w(
        // no maxLength: it would cut a pasted "EIN: 12-3456789" before einShape keeps its 9 digits
        <input {...common} inputMode="numeric" spellCheck={false}
          value={text(value)} onChange={(e) => onChange(einShape(e.target.value, text(value)))} />,
      )
    case 'phone':
      return w(<input type="tel" {...common} maxLength={30} value={text(value)} onChange={(e) => onChange(e.target.value)} />)
    case 'email':
      return w(<input {...common} type="email" autoCapitalize="none" spellCheck={false} maxLength={254} value={text(value)} onChange={(e) => onChange(e.target.value)} />)
    case 'integer':
      return w(<input {...common} inputMode="numeric" className="card-short" value={text(value)} onChange={(e) => onChange(wholeOrText(e.target.value))} />)
    case 'money':
      return w(
        <span className="card-money">
          <span aria-hidden="true">$</span>
          <input {...common} inputMode="numeric" value={money}
            onChange={(e) => { setMoney(e.target.value); const c = dollarsToCents(e.target.value); onChange(e.target.value.trim() === '' ? null : c ?? e.target.value) }} />
        </span>,
      )
    case 'percent':
      return w(
        <span className="card-money">
          <input {...common} inputMode="numeric" maxLength={4} value={text(value)} onChange={(e) => onChange(wholeOrText(e.target.value))} />
          <span aria-hidden="true">%</span>
        </span>,
      )
    case 'bool':
      return (
        <Wrap def={def} testid={testid} error={error} inline confirm={value === true}>
          <input type="checkbox" data-testid={testid} id={testid} name={section ? `${section}-${def.key}` : def.key} checked={value === true} onChange={(e) => onChange(e.target.checked)} onBlur={onBlur} />
        </Wrap>
      )
    case 'address':
      return <AddressInput {...p} />
    case 'people':
      return <PeopleInput {...p} />
  }
}

function AddressInput({ def, value, onChange, onBlur, error, testid }: Props) {
  const a: Address = { ...EMPTY_ADDRESS, ...((value as Address | null) ?? {}) }
  // A browser or password manager fills every line in one go, before React draws again: build each change on the
  // last one, not on this draw's copy, or only the last line filled would stay.
  const cur = useRef(a)
  cur.current = a
  const set = (k: keyof Address) => (e: { target: { value: string } }) => {
    cur.current = { ...cur.current, [k]: k === 'state' ? stateCode(e.target.value) : e.target.value }
    onChange(cur.current)
  }
  const box = (part: string, token: string, name: string) => ({ 'data-testid': `${testid}-${part}`, id: `${testid}-${part}`, name, autoComplete: `section-business ${token}`, onBlur })
  return (
    <Group def={def} testid={testid} error={error}>
      {/* each line's name sits under it, the way paper forms do (founder, 2026-11-02: a name above read as the line above's) */}
      <label className="card-sub"><input {...box('line1', 'address-line1', 'address1')} value={a.line1} maxLength={120} onChange={set('line1')} /><span>Street</span></label>
      <label className="card-sub"><input {...box('line2', 'address-line2', 'address2')} value={a.line2} maxLength={120} onChange={set('line2')} /><span>Suite <span className="muted">(optional)</span></span></label>
      <div className="card-address-row">
        <label className="card-sub"><input {...box('city', 'address-level2', 'city')} value={a.city} maxLength={120} onChange={set('city')} /><span>City</span></label>
        <label className="card-sub">
          <select {...box('state', 'address-level1', 'state')} value={a.state} onChange={set('state')}>
            <option value="">Choose</option>
            {(def.states ?? []).map((s) => <option key={s} value={s} label={s}>{STATE_NAMES[s] ?? s}</option>)}
          </select>
          <span>State</span>
        </label>
        <label className="card-sub"><input {...box('zip', 'postal-code', 'zip')} inputMode="numeric" value={a.zip} maxLength={10} onChange={set('zip')} /><span>ZIP</span></label>
      </div>
    </Group>
  )
}

function PeopleInput({ def, value, onChange, onBlur, error, testid, newPerson }: Props) {
  const people = (Array.isArray(value) ? value : []) as Person[]
  const max = def.max_items ?? 4
  // build on the last change, not this draw's copy: an autofill sets several lines before React draws again
  const cur = useRef(people)
  cur.current = people
  const setPerson = (i: number, key: string, v: unknown) => {
    cur.current = cur.current.map((x, j) => (j === i ? { ...x, [key]: v } : x))
    onChange(cur.current)
  }
  return (
    <Group def={def} testid={testid} error={error}>
      {people.map((person, i) => (
        <div key={person.id} className="card-person" data-testid={`${testid}-person-${i}`}>
          <div className="card-person-head">
            <h3>Owner {i + 1}</h3>
            <button type="button" className="e-btn-text" data-testid={`${testid}-remove-${i}`}
              onClick={() => onChange(people.filter((_, j) => j !== i))}>Remove</button>
          </div>
          {(def.fields ?? []).map((f) => (
            <FieldInput key={f.key} def={f} value={person[f.key]} onBlur={onBlur}
              onChange={(v) => setPerson(i, f.key, v)} testid={`${testid}-${i}-${f.key}`} section={`owner-${i + 1}`} />
          ))}
        </div>
      ))}
      {people.length < max ? (
        <button type="button" className="e-btn" data-testid={`${testid}-add`}
          onClick={() => onChange([...people, newPerson ? newPerson() : { id: newPersonId() }])}>
          Add an owner
        </button>
      ) : null}
    </Group>
  )
}
